import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { CardFace, cardInfo } from "./CardFace";
import { DicePanel } from "./DicePanel";
import { MiniMap, spaceTargets } from "./MiniMap";
import { ResponseOptions } from "./ResponseOptions";
import { PromptText, Rich, SkillTag } from "./RichText";
import css from "./Unfathomable.module.css";

export function windowKey(prompt: string): string {
  return prompt.split(/[（：]/)[0];
}

const kAutoPassKey = "unfathomable-autopass";

export function autoPassPrefs(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(kAutoPassKey) ?? "{}"); } catch { return {}; }
}

function setAutoPass(key: string, on: boolean) {
  const p = autoPassPrefs();
  p[key] = on;
  localStorage.setItem(kAutoPassKey, JSON.stringify(p));
}

export interface DecisionPanelProps {
  view: uf.GameView;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  onAnswer: (choice: string[]) => void;
  /** Re-open the action dialog (the action step is answered there). */
  onOpenActions: () => void;
  artOk: boolean;
  /** Response windows: the card ability whose play area is open. */
  respKey: string | null;
  setRespKey: (key: string | null) => void;
}

/** "influence#0", "lore#2"… : one option per card of a skill type (draw / pick distributions). */
const kDistId = /^([a-z]+)#(\d+)$/;
function isDistribution(options: uf.Option[]): boolean {
  return options.length > 0 && options.every(o => kDistId.test(o.id));
}

/** Compact "− n +" counters per skill type, capped by the decision's maximum. */
function DistributionPicker({ d, onAnswer }: { d: uf.DecisionView; onAnswer: (choice: string[]) => void }) {
  const types = useMemo(() => {
    const m = new Map<string, { label: string; cap: number }>();
    for (const o of d.options) {
      const t = o.id.match(kDistId)![1];
      const cur = m.get(t);
      m.set(t, { label: o.label.replace(/\s*\d+$/, ""), cap: (cur?.cap ?? 0) + 1 });
    }
    return [...m.entries()];
  }, [d]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => { setCounts({}); }, [d.id]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const bump = (t: string, k: number, cap: number) => setCounts(c => {
    const now = (c[t] ?? 0) + k;
    if (now < 0 || now > cap) return c;
    if (k > 0 && total >= d.max) return c;
    return { ...c, [t]: now };
  });
  const ok = total >= d.min && total <= d.max;
  const submit = () => onAnswer(types.flatMap(([t]) => Array.from({ length: counts[t] ?? 0 }, (_, i) => `${t}#${i}`)));
  return (
    <div>
      <div className={css.distRows}>
        {types.map(([t, { label, cap }]) => {
          const skill = (uf.kSkillTypes as readonly string[]).includes(t) ? (t as uf.SkillType) : null;
          const n = counts[t] ?? 0;
          return (
            <div key={t} className={css.distRow}>
              <span className={css.distName}>{skill && !label.includes("放弃") ? <SkillTag type={skill} /> : <Rich text={label} />}</span>
              <button className={`${css.btn} ${css.btnSmall}`} disabled={n <= 0} onClick={() => bump(t, -1, cap)}>−</button>
              <span className={css.distCount}>{n}</span>
              <button className={`${css.btn} ${css.btnSmall}`} disabled={n >= cap || total >= d.max} onClick={() => bump(t, 1, cap)}>+</button>
            </div>
          );
        })}
      </div>
      <div className={css.distFoot}>
        <span>已选 <b>{total}</b> / {d.min === d.max ? d.max : `${d.min}~${d.max}`}</span>
        <button className={css.btn} disabled={!ok} onClick={submit}>确认</button>
      </div>
    </div>
  );
}

/** Options that are mythos / waypoint / spell cards: shown as the cards, with their full text. */
function CardOptions({ options, artOk, rank, onPick }: {
  options: uf.DecisionView["options"]; artOk: boolean;
  /** Order mode: the position each card was given (1-based), if any. */
  rank?: (id: string) => number | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className={css.cardOptions}>
      {options.map(o => {
        const info = cardInfo(o.cardKind!, o.cardDef!);
        const r = rank?.(o.id) ?? null;
        return (
          <button key={o.id} className={css.cardOption} data-ranked={r !== null ? "1" : "0"} onClick={() => onPick(o.id)}>
            {r !== null && <span className={css.cardOptionRank}>{r}</span>}
            <CardFace kind={o.cardKind!} id={o.cardDef!} artOk={artOk} width={128} wide={o.cardKind === "waypoint"} noTip />
            <div className={css.cardOptionTitle}>{info.title}</div>
            {info.sub && <div className={css.cardOptionSub}>{info.sub}</div>}
            <div className={css.cardOptionText}><Rich text={info.text} /></div>
          </button>
        );
      })}
    </div>
  );
}

export function DecisionPanel({ view, selected, setSelected, onAnswer, onOpenActions, artOk, respKey, setRespKey }: DecisionPanelProps) {
  const d = view.decision;
  const [order, setOrder] = useState<string[]>([]);
  const [autoPass, setAutoPassState] = useState(false);
  useEffect(() => { setOrder([]); }, [d?.id]);
  useEffect(() => { if (d?.kind === "window") setAutoPassState(!!autoPassPrefs()[windowKey(d.prompt)]); }, [d?.id, d?.kind, d?.prompt]);

  const groups = useMemo(() => {
    const m = new Map<string, uf.Option[]>();
    for (const o of d?.options ?? []) m.set(o.group ?? "", [...(m.get(o.group ?? "") ?? []), o]);
    return [...m.entries()];
  }, [d]);

  if (view.gameOver) {
    return <div className={`${css.decision} ${css.frame}`}><div className={css.decisionPrompt}>游戏已结束。</div></div>;
  }
  if (!d) return null;

  if (!d.mine) {
    return (
      <div className={`${css.decision} ${d.kind === "window" || d.kind === "idle" ? css.window : ""} ${css.frame}`}>
        <div className={css.decisionPrompt}><PromptText text={d.die ? d.prompt.split("｜")[0] : d.prompt} /></div>
        {d.die && <DicePanel die={d.die} />}
        {d.answered && <div className={css.waiting}>你已作答，等待其他玩家…</div>}
        {d.waitingFor.length > 0 && <div className={css.waiting}>等待：{d.waitingFor.join("、")}</div>}
        <Hourglass />
      </div>
    );
  }

  const single = d.min === 1 && d.max === 1 && d.kind !== "order";
  const toggle = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < d.max ? [...prev, id] : prev);
  };
  const canSubmit = selected.length >= d.min && selected.length <= d.max;
  const isCardPick = d.kind === "cards" || d.kind === "contribute" || (d.options.length > 0 && d.options.every(o => o.cardUid !== undefined));

  if (d.kind === "action") {
    return (
      <div className={`${css.decision} ${css.decisionMine} ${css.frame}`}>
        <div className={css.decisionPrompt}><PromptText text={d.prompt} /></div>
        <div className={css.waiting} style={{ color: "inherit" }}>在棋盘中央的行动面板中选择。</div>
        <button className={css.btn} style={{ marginTop: 8 }} onClick={onOpenActions}>🎯 打开行动面板</button>
      </div>
    );
  }

  return (
    <motion.div key={d.id} className={`${css.decision} ${css.decisionMine} ${css.frame} ${d.kind === "window" ? css.window : ""}`}
      initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25 }}>
      <div className={css.decisionPrompt}><PromptText text={d.die ? d.prompt.split("｜")[0] : d.prompt} /></div>
      {d.die && <DicePanel die={d.die} />}

      {isDistribution(d.options) ? (
        <DistributionPicker d={d} onAnswer={onAnswer} />
      ) : d.kind === "order" ? (
        <>
          {d.options.every(o => o.cardKind && o.cardDef) ? (
            <CardOptions options={d.options} artOk={artOk} rank={id => (order.includes(id) ? order.indexOf(id) + 1 : null)}
              onPick={id => setOrder(order.includes(id) ? order.filter(x => x !== id) : [...order, id])} />
          ) : (
          <div className={css.options}>
            {d.options.map(o => {
              const idx = order.indexOf(o.id);
              return (
                <button key={o.id} className={`${css.btn} ${idx >= 0 ? css.btnSelected : ""}`}
                  onClick={() => setOrder(idx >= 0 ? order.filter(x => x !== o.id) : [...order, o.id])}>
                  {idx >= 0 ? `${idx + 1}. ` : "　 "}<Rich text={o.label} />
                </button>
              );
            })}
          </div>
          )}
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button className={css.btn} disabled={order.length !== d.options.length} onClick={() => onAnswer(order)}>确认顺序</button>
            <button className={`${css.btn} ${css.btnGhost}`} onClick={() => onAnswer(d.options.map(o => o.id))}>按默认顺序</button>
          </div>
        </>
      ) : isCardPick ? (
        <>
          {d.max > 0 && (
            <div className={css.playArea}>
              {selected.length === 0 && (
                <div className={css.playAreaHint}>点击下方手牌放到这里（需要 {d.min === d.max ? d.min : `${d.min}~${d.max}`} 张）</div>
              )}
              {selected.map(id => {
                const uid = d.options.find(o => o.id === id)?.cardUid;
                const card = view.hand.find(c => c.uid === uid);
                return card ? (
                  <CardFace key={id} kind="skill" id={card.def} artOk={artOk} width={74} layoutId={`uf-card-${card.uid}`}
                    onClick={() => toggle(id)} style={{ cursor: "pointer" }} />
                ) : null;
              })}
              {selected.length > 0 && <div className={css.playAreaCount}>{selected.length}/{d.max}<br /><span>点牌收回</span></div>}
            </div>
          )}
          {d.options.some(o => o.cardUid === undefined) && (
            <div className={css.options} style={{ marginTop: 6 }}>
              {d.options.filter(o => o.cardUid === undefined).map(o => (
                <button key={o.id} className={`${css.btn} ${selected.includes(o.id) ? css.btnSelected : ""}`} onClick={() => toggle(o.id)}><Rich text={o.label} /></button>
              ))}
            </div>
          )}
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button className={css.btn} disabled={!canSubmit} onClick={() => onAnswer(selected)}>确认</button>
            {d.min === 0 && <button className={`${css.btn} ${css.btnGhost}`} onClick={() => onAnswer([])}>不加入任何牌</button>}
          </div>
        </>
      ) : d.kind === "window" ? (
        <ResponseOptions options={d.options} hand={view.hand} artOk={artOk} active={respKey} setActive={setRespKey}
          selected={selected} setSelected={setSelected} onAnswer={onAnswer} />
      ) : (
        <>
          {single && d.options.some(o => o.cardKind) && (
            <CardOptions options={d.options.filter(o => o.cardKind && o.cardDef)} artOk={artOk} onPick={id => onAnswer([id])} />
          )}
          {single && d.options.some(o => o.space) && (
            <MiniMap view={view} artOk={artOk} targets={spaceTargets(d.options)} onPick={id => onAnswer([id])} />
          )}
          {groups.map(([g, opts]) => [g, single ? opts.filter(o => !o.cardKind) : opts] as const).filter(([, opts]) => opts.length > 0).map(([g, opts]) => (
            <div key={g} className={css.optionGroup}>
              {g && <div className={css.optionGroupTitle}>{g}</div>}
              <div className={g === "移动" ? css.optionsCompact : css.options}>
                {opts.map(o => g === "移动" ? (
                  <button key={o.id} className={`${css.btn} ${css.btnSmall}`} onClick={() => (single ? onAnswer([o.id]) : toggle(o.id))}>
                    {o.label.replace(/^移动到/, "")}
                  </button>
                ) : (
                  <button key={o.id} title={o.detail}
                    className={`${css.btn} ${selected.includes(o.id) ? css.btnSelected : ""} ${o.id === "pass" ? css.btnGhost : ""}`}
                    onClick={() => (single ? onAnswer([o.id]) : toggle(o.id))}>
                    <Rich text={o.label} />{o.detail && <div style={{ fontSize: "0.8em", fontWeight: 400, opacity: 0.8 }}>{o.detail}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!single && (
            <div style={{ marginTop: 8 }}>
              <button className={css.btn} disabled={!canSubmit} onClick={() => onAnswer(selected)}>确认（已选 {selected.length}）</button>
            </div>
          )}
        </>
      )}

      {d.kind === "window" && (
        <label style={{ display: "block", marginTop: 10, fontSize: "0.85em" }}>
          <input type="checkbox" checked={autoPass} onChange={e => { setAutoPass(windowKey(d.prompt), e.target.checked); setAutoPassState(e.target.checked); }} />
          {" "}以后自动跳过「{windowKey(d.prompt)}」类响应
        </label>
      )}
    </motion.div>
  );
}

function Hourglass() {
  // A fixed, clipped box: the spinning glyph's corners must not grow the panel (scrollbars).
  return (
    <div className={css.hourglassBox}>
      <motion.div style={{ fontSize: 26, lineHeight: 1 }}
        animate={{ rotate: [0, 0, 180, 180] }} transition={{ duration: 2.4, repeat: Infinity, times: [0, 0.4, 0.6, 1] }}>
        ⌛
      </motion.div>
    </div>
  );
}
